import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { flashcardService } from '../../services/flashcard.service';
import { FlashcardSet, FlashcardFolder, Flashcard, DictionaryResult } from '../../types/flashcard.types';
import { ArrowLeft, Plus, Trash2, Search, Volume2, Image as ImageIcon, Loader } from 'lucide-react';
import { ImageUpload } from './ImageUpload';

const SetEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Set metadata
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [languageFrom, setLanguageFrom] = useState('en');
  const [languageTo, setLanguageTo] = useState('vi');
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [visibility, setVisibility] = useState<'private' | 'public' | 'class'>('private');
  const [folderId, setFolderId] = useState<number | null>(null);
  const [folders, setFolders] = useState<FlashcardFolder[]>([]);
  const [originalSetData, setOriginalSetData] = useState<any>(null);

  // Cards
  const [cards, setCards] = useState<Partial<Flashcard>[]>([
    { front: '', back: '', phonetic: '', example: '', note: '', image_url: '', audio_url: '' }
  ]);

  // Dictionary lookup
  const [lookupWord, setLookupWord] = useState('');
  const [lookupResult, setLookupResult] = useState<DictionaryResult | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    loadFolders();
    if (isEditMode) {
      loadSet();
    }
  }, [id]);

  const loadFolders = async () => {
    try {
      const data = await flashcardService.getAllFolders();
      setFolders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const loadSet = async () => {
    try {
      setLoading(true);
      const set: FlashcardSet = await flashcardService.getSetById(Number(id));

      setName(set.name);
      setDescription(set.description || '');
      setLanguageFrom(set.language_from);
      setLanguageTo(set.language_to);
      setDifficulty(set.difficulty || 'beginner');
      setTags(set.tags || []);
      setVisibility(set.visibility || 'private');
      setFolderId(set.folder_id ?? null);
      setOriginalSetData(set);

      if (set.cards && set.cards.length > 0) {
        setCards(set.cards);
      }
    } catch (error) {
      console.error('Failed to load set:', error);
      const errorMessage = typeof error === 'string' ? error : 'Không thể tải set!';
      alert(errorMessage);
      navigate('/flashcards');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = () => {
    setCards([...cards, {
      front: '',
      back: '',
      phonetic: '',
      example: '',
      note: '',
      image_url: '',
      audio_url: ''
    }]);
  };

  const handleRemoveCard = (index: number) => {
    if (cards.length === 1) {
      alert('Phải có ít nhất 1 thẻ!');
      return;
    }
    setCards(cards.filter((_, i) => i !== index));
  };

  const handleCardChange = (index: number, field: keyof Flashcard, value: string) => {
    const newCards = [...cards];
    newCards[index] = { ...newCards[index], [field]: value };
    setCards(newCards);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleDictionaryLookup = async (cardIndex: number) => {
    const word = cards[cardIndex].front?.trim();
    if (!word) {
      alert('Vui lòng nhập từ cần tra!');
      return;
    }

    try {
      setLookupLoading(true);
      setActiveCardIndex(cardIndex);
      setLookupWord(word);

      const response = await flashcardService.lookupWord(word, languageTo);

      if (response.success) {
        const result: DictionaryResult = response.data;
        setLookupResult(result);

        // Auto-fill card data
        const newCards = [...cards];
        newCards[cardIndex] = {
          ...newCards[cardIndex],
          back: result.definition,
          phonetic: result.phonetic || newCards[cardIndex].phonetic,
          example: result.example || newCards[cardIndex].example,
          audio_url: result.audioUrl || newCards[cardIndex].audio_url,
          image_url: result.imageUrl || newCards[cardIndex].image_url,
        };
        setCards(newCards);
      } else {
        alert('Không tìm thấy từ này trong từ điển!');
        setLookupResult(null);
      }
    } catch (error) {
      console.error('Dictionary lookup failed:', error);
      alert(`Lỗi tra từ: ${error}`);
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Vui lòng nhập tên set!');
      return;
    }

    const validCards = cards.filter(card => card.front?.trim() && card.back?.trim());
    if (validCards.length === 0) {
      alert('Phải có ít nhất 1 thẻ hợp lệ (có cả front và back)!');
      return;
    }

    try {
      setSaveLoading(true);

      const setData = {
        name: name.trim(),
        description: description.trim() || undefined,
        language_from: languageFrom,
        language_to: languageTo,
        difficulty,
        tags,
        visibility: visibility,
        folder_id: folderId ?? undefined,
      };

      let setId: number;

      if (isEditMode) {
        // Update existing set
        await flashcardService.updateSet(Number(id), setData);
        setId = Number(id);

        // Delete all existing cards and create new ones
        // First get all existing cards
        const existingCards = await flashcardService.getAllCards(setId);

        // Delete each card
        if (Array.isArray(existingCards)) {
          for (const card of existingCards) {
            await flashcardService.deleteCard(card.id);
          }
        }

        // Create new cards
        const cardsToCreate = validCards.map((card, index) => ({
          front: card.front!,
          back: card.back!,
          phonetic: card.phonetic,
          example: card.example,
          note: card.note,
          image_url: card.image_url,
          audio_url: card.audio_url,
          difficulty: 'medium' as const,
        }));

        await flashcardService.bulkCreateCards(setId, cardsToCreate);
      } else {
        // Create new set
        const createResponse = await flashcardService.createSet(setData);
        setId = createResponse.id;

        // Create cards
        const cardsToCreate = validCards.map((card, index) => ({
          front: card.front!,
          back: card.back!,
          phonetic: card.phonetic,
          example: card.example,
          note: card.note,
          image_url: card.image_url,
          audio_url: card.audio_url,
          difficulty: 'medium' as const,
        }));

        await flashcardService.bulkCreateCards(setId, cardsToCreate);
      }

      alert(isEditMode ? 'Cập nhật set thành công!' : 'Tạo set thành công!');
      navigate(`/flashcards/sets/${setId}/detail`);
    } catch (error) {
      console.error('Failed to save set:', error);
      alert(`Lỗi: ${error}`);
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return <LoadingContainer>Đang tải...</LoadingContainer>;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/flashcards')}>
          <ArrowLeft size={20} />
          Quay lại
        </BackButton>
        <Title>{isEditMode ? 'Chỉnh sửa Set' : 'Tạo Set Mới'}</Title>
        <SaveButton onClick={handleSave} disabled={saveLoading}>
          {saveLoading ? 'Đang lưu...' : 'Lưu Set'}
        </SaveButton>
      </Header>

      <Content>
        <Section>
          <SectionTitle>Thông tin Set</SectionTitle>

          <FormGroup>
            <Label>Tên Set *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="VD: 100 từ vựng IELTS Band 7+"
            />
          </FormGroup>

          <FormGroup>
            <Label>Mô tả</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Mô tả về set từ vựng này..."
              rows={3}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Ngôn ngữ gốc</Label>
              <Select value={languageFrom} onChange={(e) => setLanguageFrom(e.target.value)}>
                <option value="en">English</option>
                <option value="vi">Tiếng Việt</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Ngôn ngữ đích</Label>
              <Select value={languageTo} onChange={(e) => setLanguageTo(e.target.value)}>
                <option value="vi">Tiếng Việt</option>
                <option value="en">English</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Độ khó</Label>
              <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value as any)}>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>📁 Folder</Label>
            <Select
              value={folderId ?? ''}
              onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— Không thuộc folder nào —</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.icon ? `${folder.icon} ` : ''}{folder.name}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Tags</Label>
            <TagInput>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Thêm tag (Enter để thêm)"
              />
              <AddTagButton type="button" onClick={handleAddTag}>
                Thêm
              </AddTagButton>
            </TagInput>
            <TagList>
              {tags.map((tag) => (
                <Tag key={tag}>
                  {tag}
                  <RemoveTagButton onClick={() => handleRemoveTag(tag)}>×</RemoveTagButton>
                </Tag>
              ))}
            </TagList>
          </FormGroup>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>Flashcards ({cards.length})</SectionTitle>
            <AddCardButton onClick={handleAddCard}>
              <Plus size={20} />
              Thêm thẻ
            </AddCardButton>
          </SectionHeader>

          {cards.map((card, index) => (
            <CardEditor key={index}>
              <CardHeader>
                <CardNumber>Thẻ {index + 1}</CardNumber>
                <CardActions>
                  <LookupButton
                    onClick={() => handleDictionaryLookup(index)}
                    disabled={lookupLoading && activeCardIndex === index}
                  >
                    {lookupLoading && activeCardIndex === index ? (
                      <Loader size={16} className="spin" />
                    ) : (
                      <Search size={16} />
                    )}
                    Tra từ
                  </LookupButton>
                  {cards.length > 1 && (
                    <DeleteButton onClick={() => handleRemoveCard(index)}>
                      <Trash2 size={16} />
                    </DeleteButton>
                  )}
                </CardActions>
              </CardHeader>

              <CardGrid>
                <FormGroup>
                  <Label>Front (Từ tiếng Anh) *</Label>
                  <Input
                    value={card.front || ''}
                    onChange={(e) => handleCardChange(index, 'front', e.target.value)}
                    placeholder="VD: beautiful"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Back (Nghĩa tiếng Việt) *</Label>
                  <Input
                    value={card.back || ''}
                    onChange={(e) => handleCardChange(index, 'back', e.target.value)}
                    placeholder="VD: đẹp, xinh đẹp"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phonetic (Phiên âm)</Label>
                  <Input
                    value={card.phonetic || ''}
                    onChange={(e) => handleCardChange(index, 'phonetic', e.target.value)}
                    placeholder="VD: /ˈbjuːtɪfl/"
                  />
                  {card.audio_url && (
                    <AudioPreview>
                      <Volume2 size={16} />
                      <audio controls src={card.audio_url} style={{ width: '100%', height: '30px' }} />
                    </AudioPreview>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Example (Ví dụ)</Label>
                  <Textarea
                    value={card.example || ''}
                    onChange={(e) => handleCardChange(index, 'example', e.target.value)}
                    placeholder="VD: She is a beautiful girl."
                    rows={2}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Note (Ghi chú)</Label>
                  <Textarea
                    value={card.note || ''}
                    onChange={(e) => handleCardChange(index, 'note', e.target.value)}
                    placeholder="Ghi chú thêm..."
                    rows={2}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>🖼️ Ảnh</Label>
                  <ImageUpload
                    value={card.image_url || ''}
                    onChange={(value) => handleCardChange(index, 'image_url', value)}
                  />
                </FormGroup>
              </CardGrid>

              {lookupResult && activeCardIndex === index && (
                <DictionaryResultBox>
                  <ResultTitle>📖 Kết quả tra từ: {lookupResult.word}</ResultTitle>
                  {lookupResult.phonetic && <ResultPhonetic>{lookupResult.phonetic}</ResultPhonetic>}
                  <ResultDefinition>{lookupResult.definition}</ResultDefinition>
                  {lookupResult.example && <ResultExample>Ví dụ: {lookupResult.example}</ResultExample>}
                  {lookupResult.suggestions && lookupResult.suggestions.length > 0 && (
                    <ResultSuggestions>
                      <strong>Các nghĩa khác:</strong>
                      <ul>
                        {lookupResult.suggestions.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </ResultSuggestions>
                  )}
                </DictionaryResultBox>
              )}
            </CardEditor>
          ))}
        </Section>
      </Content>
    </Container>
  );
};

// Styled Components (abbreviated for space)
const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #edf2f7;
  color: #2d3748;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
  }
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  flex: 1;
  text-align: center;
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1.5rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TagInput = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const AddTagButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #5568d3;
  }
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #edf2f7;
  border-radius: 20px;
  font-size: 0.875rem;
  color: #2d3748;
`;

const RemoveTagButton = styled.button`
  background: none;
  border: none;
  color: #718096;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #e53e3e;
  }
`;

const AddCardButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #5568d3;
  }
`;

const CardEditor = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 2px solid #e2e8f0;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardNumber = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const LookupButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #48bb78;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #38a169;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: #fc8181;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f56565;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const AudioPreview = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: white;
  border-radius: 8px;
`;

const ImagePreview = styled.div`
  margin-top: 0.5rem;
  
  img {
    width: 100%;
    max-height: 150px;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const DictionaryResultBox = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #e6fffa;
  border-left: 4px solid #38b2ac;
  border-radius: 8px;
`;

const ResultTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #234e52;
  margin-bottom: 0.5rem;
`;

const ResultPhonetic = styled.div`
  font-size: 0.875rem;
  color: #2c7a7b;
  font-style: italic;
  margin-bottom: 0.5rem;
`;

const ResultDefinition = styled.div`
  font-size: 0.875rem;
  color: #2d3748;
  margin-bottom: 0.5rem;
`;

const ResultExample = styled.div`
  font-size: 0.875rem;
  color: #4a5568;
  font-style: italic;
  margin-bottom: 0.5rem;
`;

const ResultSuggestions = styled.div`
  font-size: 0.875rem;
  color: #2d3748;
  
  ul {
    margin: 0.5rem 0 0 1.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 1.25rem;
  color: #718096;
`;

export default SetEditor;
